import React from "react";
import Column from "@amzn/meridian/column";
import Checkbox from "@amzn/meridian/checkbox";
import DataTable, {
  DataTableBody,
  DataTableCaption,
  DataTableHeader,
  DataTableHead,
  DataTableRow,
} from "@amzn/meridian/data-table";
import { AuditResult } from "src/store/auditSlice";
import { Incident } from "./types";
import AuditTableRow from "./AuditTableRow";

interface AuditTableProps {
  incidents: Incident[];
  visibleIncidents: Incident[];
  selectedRows: string[];
  onToggleRowSelection: (incidentId: string) => void;
  onToggleAllRows: () => void;
  showAllFields: boolean;
  includeAllFields?: boolean;
  onSort?: ({ sortColumn, sortDirection }: { sortColumn: string; sortDirection: string }) => void;
  sortColumn?: string;
  sortDirection?: string;
  tabType?: "all" | "human" | "ai";
  caption: string;
  selectedBusinessUnits?: string[];
  selectedOrganizations?: string[];
  selectedScopes?: string[];
  selectedSiteTypes?: string[];
  selectedSites?: string[];
}

const AuditTable: React.FC<AuditTableProps> = ({
  incidents,
  visibleIncidents,
  selectedRows,
  onToggleRowSelection,
  onToggleAllRows,
  showAllFields,
  includeAllFields = false,
  onSort,
  sortColumn,
  sortDirection,
  tabType = "all",
  caption,
  selectedBusinessUnits = [],
  selectedOrganizations = [],
  selectedScopes = [],
  selectedSiteTypes = [],
  selectedSites = [],
}) => {
  const getFilteredIncidents = () => {
    switch (tabType) {
      case "human":
        return incidents.filter((incident) =>
          incident.fields.some(
            (field) =>
              field.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
              field.auditResult === AuditResult.LLM_CORRECT,
          ),
        );
      case "ai":
        return incidents.filter((incident) =>
          incident.fields.some(
            (field) =>
              field.auditResult === AuditResult.HUMAN_CORRECT ||
              field.auditResult === AuditResult.NEITHER,
          ),
        );
      default:
        return incidents;
    }
  };

  const filteredIncidents = getFilteredIncidents();
  const isAllSelected = selectedRows.length === filteredIncidents.length;

  return (
    <Column height="65vh" overflowY="auto">
      <DataTable
        onSort={onSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection as any}
        showRowDividers={false}
        showColumnDividers={false}
        showStripes={false}
        showBorder={false}
        indentColumn={1}
      >
        <DataTableCaption position="bottom">{caption}</DataTableCaption>
        <DataTableHead backgroundColor="secondary">
          <DataTableRow highlightOnHover={false}>
            <DataTableHeader>
              <Checkbox checked={isAllSelected} onChange={onToggleAllRows} />
            </DataTableHeader>
            <DataTableHeader sortColumn="caseId">Case ID</DataTableHeader>
            <DataTableHeader sortColumn="defects">Defects</DataTableHeader>
            <DataTableHeader sortColumn="dqo">DQO #</DataTableHeader>
            <DataTableHeader sortColumn="aiDefect">AI Defect #</DataTableHeader>
            <DataTableHeader sortColumn="aiSource">AI Source</DataTableHeader>
            <DataTableHeader sortColumn="reviewer">Reviewer</DataTableHeader>
            {selectedSiteTypes.length > 0 && (
              <DataTableHeader sortColumn="siteType">Site Type</DataTableHeader>
            )}
            {selectedSites.length > 0 && <DataTableHeader sortColumn="site">Site</DataTableHeader>}
            {selectedBusinessUnits.length > 0 && (
              <DataTableHeader sortColumn="obr_bu">Business Unit</DataTableHeader>
            )}
            {selectedOrganizations.length > 0 && (
              <DataTableHeader sortColumn="obr_org">Organization</DataTableHeader>
            )}
            {selectedScopes.length > 0 && (
              <DataTableHeader sortColumn="obr_scope">Scope</DataTableHeader>
            )}
          </DataTableRow>
        </DataTableHead>
        <DataTableBody>
          {visibleIncidents.map((incident, index) =>
            AuditTableRow({
              incident,
              index,
              selectedRows,
              onToggleRowSelection,
              showAllFields,
              includeAllFields,
              tabType,
              expandAllRows: showAllFields,
              selectedBusinessUnits,
              selectedOrganizations,
              selectedScopes,
              selectedSiteTypes,
              selectedSites,
            }),
          )}
        </DataTableBody>
      </DataTable>
    </Column>
  );
};

export default AuditTable;
